/**
 * Cliente HTTP del POS.
 *
 * Responsabilidades, en orden de importancia:
 *
 * 1. **Anteponer la URL base.** Vacío = mismo origen (proxy de Vite en desarrollo,
 *    Caddy en producción).
 * 2. **Poner el token** y, si el servidor responde 401, **refrescar y reintentar
 *    una sola vez**. El cajero no debe ver nunca "sesión expirada" a media venta.
 * 3. **Generar el `X-Request-ID`** de cada petición: es lo que permite encontrar
 *    en los logs la venta que "no se cobró".
 * 4. **Traducir el sobre de error** de la API a una excepción tipada.
 */

import { apiUrl, newRequestId } from "./http";
import { clearSession, getSession, refreshSession } from "./session";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | null;

  constructor(
    status: number,
    payload: { code: string; message: string; details?: unknown; request_id?: string | null },
  ) {
    super(payload.message);
    this.name = "ApiError";
    this.status = status;
    this.code = payload.code;
    this.details = payload.details ?? null;
    this.requestId = payload.request_id ?? null;
  }

  /** ¿El problema es de red o del servidor, y por tanto reintentable? */
  get isTransient(): boolean {
    return this.status >= 500 || this.status === 0;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Se reenvía en el reintento; el POS la genera una vez por venta. */
  idempotencyKey?: string;
  /** Evita el refresco automático (para el propio login y el refresco). */
  skipAuthRefresh?: boolean;
}

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

async function parseError(response: Response, requestId: string): Promise<ApiError> {
  try {
    const payload = (await response.json()) as {
      error?: { code: string; message: string; details?: unknown; request_id?: string | null };
    };
    if (payload.error) {
      return new ApiError(response.status, payload.error);
    }
  } catch {
    // Respuesta sin JSON (proxy caído, HTML de error).
  }
  return new ApiError(response.status, {
    code: "unknown_error",
    message: `La API respondió ${response.status}`,
    request_id: requestId,
  });
}

async function send(path: string, options: RequestOptions, attempt = 0): Promise<Response> {
  const { body, idempotencyKey, skipAuthRefresh, headers, ...rest } = options;
  const requestId = newRequestId();
  const session = getSession();

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    "X-Request-ID": requestId,
    ...(headers as Record<string, string> | undefined),
  };
  if (body !== undefined) finalHeaders["Content-Type"] = "application/json";
  if (session) finalHeaders.Authorization = `Bearer ${session.tokens.access_token}`;
  if (idempotencyKey) finalHeaders["Idempotency-Key"] = idempotencyKey;

  const response = await fetch(apiUrl(path), {
    ...rest,
    headers: finalHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // 401 con un token que no es válido: se renueva y se reintenta UNA vez.
  if (response.status === 401 && !skipAuthRefresh && attempt === 0 && session) {
    const renewed = await refreshSession(BASE_URL);
    if (renewed) {
      return send(path, options, attempt + 1);
    }
    clearSession();
  }

  return response;
}

/** Petición tipada. Lanza `ApiError` si la respuesta no es 2xx. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  if (!response.ok) {
    throw await parseError(response, response.headers.get("X-Request-ID") ?? "");
  }
  if (response.status === 204 || response.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/** Igual que `apiRequest` pero devuelve también las cabeceras (idempotencia). */
export async function apiRequestWithHeaders<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; headers: Headers }> {
  const response = await send(path, options);
  if (!response.ok) {
    throw await parseError(response, response.headers.get("X-Request-ID") ?? "");
  }
  const data = (response.status === 204 ? undefined : await response.json()) as T;
  return { data, headers: response.headers };
}

/** ¿Hay red? Se usa para decidir si encolar una venta o mandarla. */
export function isOnline(): boolean {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

// ---------------------------------------------------------------------------
// Salud del sistema
// ---------------------------------------------------------------------------

export interface Readiness {
  status: "ok" | "degraded";
  checks: Record<string, { status: string; detail: string | null }>;
}

/**
 * Sonda de disponibilidad.
 *
 * Distingue "la API vive pero la base no" de "no hay red", y esa diferencia es
 * la que permite decirle al cajero algo útil en vez de un error genérico.
 */
export function fetchReadiness(signal?: AbortSignal): Promise<Readiness> {
  return apiRequest<Readiness>("/health/ready", {
    skipAuthRefresh: true,
    cache: "no-store",
    signal,
  });
}
